import { assertNever } from '@kofno/piper';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { L, T, TranslationsContext, translation } from '../../../../../Translations';
import BigCheckmarkCircleIcon from '../../../../Cutesies/BigCheckmarkCircleIcon';
import WarningIcon from '../../../../Cutesies/WarningIcon';
import Modal from '../../../../Modal';
import { utcDateToLocalDate } from '../../Functions';
import { courseCount, openCourseCatalog, selectedRun } from '../CourseField';
import { foldInviteTarget, inviteTarget } from '../InviteTarget';
import { participantSearchStore } from '../ParticipantSearch/Stores/ParticipantSearchStore';
import { shareStore } from '../Stores/ShareStore';

interface Props {
  collectionName: string;
  scheduledDate: Maybe<string>;
}

// 'sent' - everyone was added; 'partial' - some were not; 'failed' - nobody was.
type Outcome = 'sent' | 'partial' | 'failed';

const outcomeOf = (hasSuccesses: boolean, hasFailures: boolean): Outcome =>
  !hasFailures ? 'sent' : hasSuccesses ? 'partial' : 'failed';

const OutcomeIcon = ({ outcome }: { outcome: Outcome }) => {
  switch (outcome) {
    case 'sent':
      return <BigCheckmarkCircleIcon width="80" height="80" />;
    case 'partial':
    case 'failed':
      return <WarningIcon />;
    default:
      return assertNever(outcome);
  }
};

const outcomeTitle = (outcome: Outcome) => {
  switch (outcome) {
    case 'sent':
      return <T kind="Invitations Scheduled" />;
    case 'partial':
      return <T kind="Invitations Partially Scheduled" />;
    case 'failed':
      return <T kind="Some participants could not be added" />;
    default:
      return assertNever(outcome);
  }
};

function SummaryRow({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5" style={{ fontSize: 'var(--text-sm)' }}>
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right" style={{ fontWeight: 'var(--font-weight-medium)' }}>
        {value}
      </span>
    </div>
  );
}

// The result of an invite, in the same chrome as the Add Group and Edit Group
// success modals (GroupSuccessModal): checkmark, heading, what it was for, a
// summary box, and the next steps stacked below (DVLP-7976).
const Confirmation: React.FC<Props> = ({ collectionName, scheduledDate }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const hasFailures = shareStore.failedParticipantCount > 0;
  const hasSuccesses = shareStore.successfulParticipantCount > 0;
  const outcome = outcomeOf(hasSuccesses, hasFailures);
  const target = inviteTarget(shareStore.state);
  const isCourseInvite = target
    .map((invite) =>
      foldInviteTarget(
        invite,
        () => false,
        () => true,
      ),
    )
    .getOrElseValue(false);

  const handleReset = () => {
    shareStore.reset();
    participantSearchStore.reset();
  };

  // DVLP-7976: same page, emptied, with no course chosen and the catalog open.
  const inviteToDifferentCourse = () => {
    handleReset();
    const params = new URLSearchParams(searchParams);
    params.delete('programId');
    setSearchParams(params, { replace: true });
    setTimeout(openCourseCatalog, 0);
  };

  const returnToCollections = () => {
    handleReset();
    const returnPath = searchParams.get('returnPath') || '/learning_collections';
    navigate(returnPath);
  };

  const invitedTo = target
    .map((invite) =>
      foldInviteTarget(
        invite,
        (collection) => collection.collectionName,
        (course) => course.courseName.getOrElseValue(course.collectionName),
      ),
    )
    .getOrElseValue(collectionName);

  // Only a course invite has a start; a run starts on its date, anything else
  // whenever the participant begins.
  const starts: Maybe<ReactNode> = selectedRun(shareStore.state).map((run) => {
    switch (run.kind) {
      case 'scheduled':
        return <L localizeable={utcDateToLocalDate(run.startOn)} format="short-month-day-year" />;
      case 'on-demand':
        return <T kind="Start anytime" />;
      default:
        return assertNever(run.kind);
    }
  });

  // Without a scheduled time no email goes out; participants simply get access.
  const email: ReactNode = shareStore.scheduleEnabled ? (
    scheduledDate.getOrElse(() => '')
  ) : (
    <T kind="Not sent" />
  );

  return (
    <Modal close={handleReset} title="" modalWidth="w-[480px]">
      <div className="text-center" data-intercom-target="share-invite-confirmation">
        <div className="flex justify-center mb-4">
          <OutcomeIcon outcome={outcome} />
        </div>

        <h2 className="text-xl mb-0.5" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
          {outcomeTitle(outcome)}
        </h2>

        <p className="text-muted-foreground mb-4" style={{ fontSize: 'var(--text-sm)' }}>
          {invitedTo}
        </p>

        {hasSuccesses && (
          <div className="bg-muted rounded-lg px-3.5 py-2.5 mb-4 text-left">
            <SummaryRow
              label={<T kind="Participants" />}
              value={shareStore.successfulParticipantCount}
            />
            {isCourseInvite &&
              starts
                .map((value) => (
                  <SummaryRow key="starts" label={<T kind="Course Start" />} value={value} />
                ))
                .getOrElseValue(<></>)}
            <SummaryRow label={<T kind="Email" />} value={email} />
          </div>
        )}

        {hasFailures && (
          <div
            className="bg-muted rounded-lg px-3.5 py-2.5 mb-4 text-left"
            style={{ fontSize: 'var(--text-sm)' }}
            data-intercom-target="share-invite-confirmation-failures"
          >
            <p
              className="text-destructive mb-2"
              style={{ fontWeight: 'var(--font-weight-medium)' }}
            >
              <T
                kind="{{count}} participant could not be added:"
                count={shareStore.failedParticipantCount}
              />
            </p>
            <TranslationsContext.Consumer>
              {(ts) => (
                <ul className="list-disc space-y-1.5 pl-5">
                  {shareStore.failedParticipants.map((failed, index) => {
                    const identifier = failed.email.getOrElse(() =>
                      failed.userId
                        .map((userId: number) => `User ID ${userId}`)
                        .getOrElse(() => translation('Unknown participant')(ts)),
                    );
                    return (
                      <li key={index}>
                        {identifier}: {failed.error}
                      </li>
                    );
                  })}
                </ul>
              )}
            </TranslationsContext.Consumer>
          </div>
        )}

        {/* The Add Group success modal's three tiers: filled for the new next step,
            outlined to repeat, text to leave. */}
        <div className="flex flex-col gap-1.5">
          {isCourseInvite && courseCount(shareStore.state) > 1 && (
            <button
              type="button"
              className="w-full py-2.5 px-4 rounded-lg bg-accent text-accent-foreground hover:opacity-90 transition-opacity"
              style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)' }}
              onClick={inviteToDifferentCourse}
              data-intercom-target="share-confirmation-different-course-button"
            >
              <T kind="Invite to Another Course" />
            </button>
          )}
          <button
            type="button"
            className="w-full py-2.5 px-4 rounded-lg border-2 border-primary text-primary bg-transparent hover:opacity-90 transition-opacity"
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)' }}
            onClick={handleReset}
            data-intercom-target="share-confirmation-invite-more-button"
          >
            {target
              .map((invite) =>
                foldInviteTarget(
                  invite,
                  () => <T kind="Invite More to This Collection" />,
                  () => <T kind="Invite More to This Course" />,
                ),
              )
              .getOrElse(() => (
                <T kind="Invite More Participants" />
              ))}
          </button>
          <button
            type="button"
            className="w-full py-2 text-muted-foreground hover:text-foreground transition-colors"
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
            onClick={returnToCollections}
            data-intercom-target="share-confirmation-return-button"
          >
            <T kind="Return to Collections" />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default observer(Confirmation);
